import { useState, useEffect } from 'react';
import { ref, get, set } from 'firebase/database';
import { HiOutlineUserAdd, HiOutlineUserGroup } from 'react-icons/hi';
import { db } from '../firebase';
import { useAdminToast } from '../admin/AdminLayout';
import { PENDING_USERS_REF, USERS_REF, ADMINS_REF, ALLOWED_USERS_REF } from '../admin/adminConstants';

function AdminUsers({ user }) {
  const { showToast } = useAdminToast();
  const [pendingUsers, setPendingUsers] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [adminUids, setAdminUids] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [activeUsersLoading, setActiveUsersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actingUid, setActingUid] = useState(null);
  const [actingActiveUid, setActingActiveUid] = useState(null);
  const [pendingListChangedKey, setPendingListChangedKey] = useState(0);
  const [activeListChangedKey, setActiveListChangedKey] = useState(0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const snapshot = await get(ref(db, PENDING_USERS_REF));
        const data = snapshot.val();
        if (!cancelled) {
          setPendingUsers(typeof data === 'object' && data !== null ? data : {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load pending users');
          setPendingUsers({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, activeListChangedKey]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setActiveUsersLoading(true);
      try {
        const [usersSnap, adminsSnap] = await Promise.all([
          get(ref(db, USERS_REF)),
          get(ref(db, ADMINS_REF)),
        ]);
        if (!cancelled) {
          const users = usersSnap.val();
          const admins = adminsSnap.val();
          setActiveUsers(typeof users === 'object' && users !== null ? users : {});
          setAdminUids(new Set(
            typeof admins === 'object' && admins !== null ? Object.keys(admins) : []
          ));
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load active users');
      } finally {
        if (!cancelled) setActiveUsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, pendingListChangedKey]);

  const handleAccept = async (uid) => {
    const data = pendingUsers?.[uid];
    if (!data || typeof data !== 'object') return;
    setActingUid(uid);
    setError(null);
    try {
      await set(ref(db, `${USERS_REF}/${uid}`), data);
      await set(ref(db, `${ALLOWED_USERS_REF}/${uid}`), true);
      await set(ref(db, `${PENDING_USERS_REF}/${uid}`), null);
      setPendingUsers((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
      setPendingListChangedKey((k) => k + 1);
      showToast('User accepted');
    } catch (err) {
      setError(err.message || 'Failed to accept user');
    } finally {
      setActingUid(null);
    }
  };

  const handleIgnore = async (uid) => {
    setActingUid(uid);
    setError(null);
    try {
      await set(ref(db, `${PENDING_USERS_REF}/${uid}`), null);
      setPendingUsers((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
      setPendingListChangedKey((k) => k + 1);
      showToast('User rejected');
    } catch (err) {
      setError(err.message || 'Failed to ignore user');
    } finally {
      setActingUid(null);
    }
  };

  const handleMoveToPending = async (uid) => {
    setActingActiveUid(uid);
    setError(null);
    try {
      const userSnap = await get(ref(db, `${USERS_REF}/${uid}`));
      const data = userSnap.val();
      if (!data || typeof data !== 'object') {
        setError('User data not found');
        return;
      }
      await set(ref(db, `${PENDING_USERS_REF}/${uid}`), data);
      await set(ref(db, `${USERS_REF}/${uid}`), null);
      await set(ref(db, `${ALLOWED_USERS_REF}/${uid}`), null);
      setActiveUsers((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
      setActiveListChangedKey((k) => k + 1);
      showToast('User moved to pending');
    } catch (err) {
      setError(err.message || 'Failed to move user to pending');
    } finally {
      setActingActiveUid(null);
    }
  };

  const handleDeleteUser = async (uid) => {
    setActingActiveUid(uid);
    setError(null);
    try {
      await set(ref(db, `${USERS_REF}/${uid}`), null);
      setActiveUsers((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
      setActiveListChangedKey((k) => k + 1);
      showToast('User deleted');
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setActingActiveUid(null);
    }
  };

  const entries = pendingUsers && typeof pendingUsers === 'object'
    ? Object.entries(pendingUsers).filter(([, v]) => v != null && typeof v === 'object')
    : [];
  const activeEntries = activeUsers && typeof activeUsers === 'object'
    ? Object.entries(activeUsers).filter(([uid, v]) => v != null && typeof v === 'object' && uid)
    : [];

  return (
    <>
      {error && (
        <div className="admin-error" role="alert">
          {error}
        </div>
      )}
      <div className="admin-cards">
        <section className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">
              <HiOutlineUserAdd className="admin-card-icon" aria-hidden />
              Pending users
            </h2>
            <span className="admin-card-count">{entries.length}</span>
          </div>
          <div className="admin-card-body">
            {loading ? (
              <p className="admin-placeholder">Loading…</p>
            ) : entries.length === 0 ? (
              <p className="admin-empty">No pending users.</p>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>UID</th>
                      <th className="admin-table-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(([uid, data]) => (
                      <tr key={uid}>
                        <td>{data.name || '—'}</td>
                        <td>{data.email || '—'}</td>
                        <td><code className="admin-uid">{uid}</code></td>
                        <td className="admin-table-actions">
                          <button type="button" className="btn btn-primary btn-sm" onClick={() => handleAccept(uid)} disabled={actingUid !== null} title="Accept">Accept</button>
                          <button type="button" className="btn btn-outline btn-sm" onClick={() => handleIgnore(uid)} disabled={actingUid !== null} title="Ignore" aria-label={`Ignore ${data.email || uid}`}>Ignore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className="admin-card">
          <div className="admin-card-header">
            <h2 className="admin-card-title">
              <HiOutlineUserGroup className="admin-card-icon" aria-hidden />
              Active users
            </h2>
            <span className="admin-card-count">{activeEntries.length}</span>
          </div>
          <div className="admin-card-body">
            {activeUsersLoading ? (
              <p className="admin-placeholder">Loading…</p>
            ) : activeEntries.length === 0 ? (
              <p className="admin-empty">No active users.</p>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>UID</th>
                      <th>Role</th>
                      <th className="admin-table-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeEntries.map(([uid, data]) => {
                      const isAdminUser = adminUids.has(uid);
                      return (
                        <tr key={uid}>
                          <td>{data.name || '—'}</td>
                          <td>{data.email || '—'}</td>
                          <td><code className="admin-uid">{uid}</code></td>
                          <td>{isAdminUser ? <span className="admin-badge">Admin</span> : '—'}</td>
                          <td className="admin-table-actions">
                            {!isAdminUser && (
                              <>
                                <button type="button" className="btn btn-outline btn-sm" onClick={() => handleMoveToPending(uid)} disabled={actingActiveUid !== null} title="Move to pending">Move to pending</button>
                                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(uid)} disabled={actingActiveUid !== null} title="Delete" aria-label={`Delete ${data.email || uid}`}>Delete</button>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

export default AdminUsers;
